import React, { useState } from "react";
import { api } from "../utils/api";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import { useRefreshSessionToken } from "../hooks/useRefreshSessionToken";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
} from "@mui/material";
import { COLORS } from "../utils/colors";

/**
 * Admin-only: align validUntil / nextBilling / QR from Stripe current_period_end.
 */
const AdminResyncFromStripe = ({
  subscriptionId,
  hasStripeId,
  onSuccess,
  compact,
  buttonLabel = "Resync from Stripe",
  size = "medium",
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const refreshSessionToken = useRefreshSessionToken();

  if (!hasStripeId) return null;

  const handleResync = async () => {
    if (!subscriptionId) return;
    setLoading(true);
    try {
      const refreshed = await refreshSessionToken();
      if (!refreshed) {
        showErrorToast(
          "Could not get an API token. Refresh the page, or sign out and sign in again if this keeps happening."
        );
        setLoading(false);
        return;
      }
      const response = await api(`v1/subscription/${subscriptionId}/resync-from-stripe`, "POST", {});
      if (response?.data?.success) {
        const d = response.data.data;
        showSuccessToast(
          `Synced from Stripe (${d?.stripeStatus ?? "ok"}). Valid until updated.`
        );
        setOpen(false);
        if (typeof onSuccess === "function") onSuccess();
      } else {
        showErrorToast(response?.data?.message || "Resync failed");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.data?.error ||
        err?.message ||
        "Resync failed";
      showErrorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        size={compact ? "small" : size}
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{
          borderColor: COLORS.priorityBlue?.hex || "#1976d2",
          color: COLORS.priorityBlue?.hex || "#1976d2",
          fontSize: compact ? "0.7rem" : undefined,
          py: compact ? 0.25 : undefined,
          minWidth: compact ? 0 : undefined,
          textTransform: "none",
          whiteSpace: "nowrap",
        }}
      >
        {buttonLabel}
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Resync from Stripe</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Updates <strong>Valid until</strong>, <strong>Next billing</strong>, and the <strong>QR code</strong> from
            Stripe&apos;s current billing period end (<code>current_period_end</code>). Also sets Active from Stripe
            status (active / trialing).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResync}
            disabled={loading}
            sx={{ backgroundColor: COLORS.priorityBlue?.hex || "#1976d2" }}
          >
            {loading ? (
              <>
                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> Syncing…
              </>
            ) : (
              "Confirm resync"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminResyncFromStripe;
